import { Injectable, inject, OnDestroy } from '@angular/core';
import {
  Auth,
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithPhoneNumber,
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  sendSignInLinkToEmail,
  PhoneAuthProvider,
  RecaptchaVerifier,
} from '@angular/fire/auth';
import { Database, ref, onValue, off } from '@angular/fire/database';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private auth: Auth = inject(Auth);
  private database: Database = inject(Database);
  private router: Router = inject(Router);
  private deletionListenerRef: any = null;

  doFacebookLogin() {
    return new Promise<any>((resolve, reject) => {
      let provider = new FacebookAuthProvider();
      let auth = this.auth;
      signInWithPopup(auth, provider).then(
        (res) => {
          resolve(res);
        },
        (err) => {
          console.log(err);
          reject(err);
        }
      );
    });
  }

  doGoogleLogin() {
    return new Promise<any>((resolve, reject) => {
      let provider = new GoogleAuthProvider();
      let auth = this.auth;
      provider.addScope('profile');
      provider.addScope('email');
      signInWithPopup(auth, provider).then(
        (res) => {
          resolve(res);
        },
        (err) => {
          console.log(err);
          reject(err);
        }
      );
    });
  }

  newRecaptchaVerifier(container: string) {
    let auth = getAuth();
    console.log(
      'Auth Service: newRecaptchaVerifier(), container',
      container,
      auth,
      {
        size: 'normal',
        callback: (response: any) => {
          console.log(
            'reCAPTCHA solved, allow signInWithPhoneNumber.',
            response
          );
        },
      }
    );
    return new RecaptchaVerifier(auth, container);
  }

  doPhoneLogin(phoneNumber: string, appVerifier: RecaptchaVerifier) {
    return new Promise<any>((resolve, reject) => {
      let auth = this.auth;
      let provider = new PhoneAuthProvider(auth);

      signInWithPhoneNumber(auth, phoneNumber, appVerifier).then(
        (res) => {
          console.log('Auth Service: doPhoneLogin(), SMS sent', res);
          resolve(res);
        },
        (err) => {
          console.log(err);
          reject(err);
        }
      );
    });
  }

  doRegister(value: any) {
    return new Promise<any>((resolve, reject) => {
      createUserWithEmailAndPassword(
        this.auth,
        value.email,
        value.password
      ).then(
        (res) => {
          resolve(res);
        },
        (err) => reject(err)
      );
    });
  }

  doLogin(value: any) {
    // if environment.useEmulators is true, use signInWithEmailAndPassword
    if (environment.useEmulators) {
      return this.doSignInWtihEmailAndPassword(value);
    } else {
      return this.doSignInViaEmailLink(value);
    }
  }

  doSignInWtihEmailAndPassword(value: any) {
    return new Promise<any>((resolve, reject) => {
      signInWithEmailAndPassword(this.auth, value.email, value.password).then(
        (res) => {
          console.log(
            'Auth Service: doSignInWithEmailAndPassword(), Logged in',
            res,
            value.email
          );
          resolve(res);
        },
        (err) => {
          console.log(
            'Auth Service: doSignInWithEmailAndPassword(): error',
            err
          );
          reject(err);
        }
      );
    });
  }

  doSignInViaEmailLink(value: any) {
    const actionCodeSettings = {
      url: location.origin + '/#/login-return',
      handleCodeInApp: true,
    };
    return new Promise<any>((resolve, reject) => {
      sendSignInLinkToEmail(this.auth, value.email, actionCodeSettings).then(
        (res) => {
          console.log(
            'Auth Service: doLogin(), Email sent',
            res,
            value.email,
            actionCodeSettings
          );
          localStorage.setItem('emailForSignIn', value.email);
          resolve(res);
        },
        (err) => {
          console.log('Auth Service: doLogin(), Email not sent', err);
          reject(err);
        }
      );
    });
  }

  doLogout() {
    return new Promise((resolve, reject) => {
      if (this.auth.currentUser) {
        this.auth.signOut();
        resolve({ success: true });
      } else {
        reject({ success: false });
      }
    });
  }

  /**
   * Initialize deletion listener for the current user
   * This listens for admin-initiated account deletions and forces immediate logout
   */
  initializeDeletionListener(uid: string): void {
    if (!uid) {
      console.warn('[AuthService] Cannot initialize deletion listener without uid');
      return;
    }

    // Clean up previous listener if exists
    this.cleanupDeletionListener();

    try {
      this.deletionListenerRef = ref(this.database, `user-deletions/${uid}`);

      onValue(this.deletionListenerRef, (snapshot) => {
        const deletionSignal = snapshot.val();

        if (deletionSignal) {
          console.log('[AuthService] Account deletion detected, forcing logout', deletionSignal);

          // Force immediate logout
          this.forceLogout('Your account has been deleted by an administrator');
        }
      }, (error) => {
        console.error('[AuthService] Error in deletion listener:', error);
      });

      console.log('[AuthService] Deletion listener initialized for user:', uid);
    } catch (error) {
      console.error('[AuthService] Failed to initialize deletion listener:', error);
    }
  }

  /**
   * Force logout and navigate to login page with a message
   */
  private async forceLogout(message: string): Promise<void> {
    try {
      // Clean up listener first
      this.cleanupDeletionListener();

      // Sign out from Firebase
      await this.auth.signOut();

      // Clear all local storage
      localStorage.clear();
      sessionStorage.clear();

      // Show alert to user
      alert(message);

      // Navigate to login page
      this.router.navigate(['/login']);

      console.log('[AuthService] User forcefully logged out due to account deletion');
    } catch (error) {
      console.error('[AuthService] Error during forced logout:', error);
      // Force page reload as last resort
      window.location.href = '/#/login';
    }
  }

  /**
   * Clean up deletion listener
   */
  private cleanupDeletionListener(): void {
    if (this.deletionListenerRef) {
      try {
        off(this.deletionListenerRef);
        this.deletionListenerRef = null;
        console.log('[AuthService] Deletion listener cleaned up');
      } catch (error) {
        console.error('[AuthService] Error cleaning up deletion listener:', error);
      }
    }
  }

  /**
   * OnDestroy lifecycle hook
   */
  ngOnDestroy(): void {
    this.cleanupDeletionListener();
  }
}
